'use client'

import { Post } from '@/payload-types'
import { Locale } from '@/providers/Locale/types'
import { getPostArray, postToArticle } from '@/utilities/postToArticle'
import { cn } from '@/utilities/ui'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { MouseEventHandler, useCallback, useRef, useState } from 'react'

interface Props {
    posts: Array<Post>
    category: string
    locale: Locale
    link: string
}

type ScrollDistance = 'min' | 'mid' | 'max'

export const VideoCategoryPlaylist: React.FC<Props> = ({ posts, category, locale, link }) => {
    const videoListDivRef = useRef<HTMLDivElement>(null)
    const [scrollDistance, setScrollDistance] = useState<ScrollDistance>('min')

    const handleNavigationClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        (e) => {
            const container = videoListDivRef.current
            if (!container) {
                throw new Error('Video list ref is not set')
            }

            const direction = e.currentTarget.dataset['direction'] === 'left' ? -1 : 1
            const maxScrollDistance = container.scrollWidth - container.clientWidth
            const distance = Math.min(maxScrollDistance, Math.max(container.scrollLeft + container.clientWidth * direction, 0))

            container.scroll({
                left: distance,
                behavior: 'smooth',
            })

            if (distance === 0) {
                setScrollDistance('min')
            } else if (distance >= maxScrollDistance) {
                setScrollDistance('max')
            } else {
                setScrollDistance('mid')
            }
        },
        [videoListDivRef, setScrollDistance],
    )
    // { 'md:invisible': scrollDistance === 'min' }
    return (
        <div id={link} className='subCategoryBlock mt-8'>
            <div className='flex justify-between mb-4'>
                <h3 className='font-bold text-3xl'>{category}</h3>
            </div>
            <div className='flex flex-row relative'>
                <button
                    type='button'
                    aria-label='Scroll left'
                    className={cn(
                        'hidden md:flex h-[238px] items-center hover:cursor-pointer pb-16 border-0 bg-transparent absolute top-0 left-0 -translate-x-full',
                        {
                            'md:invisible': scrollDistance === 'min',
                        },
                    )}
                    data-direction='left'
                    onClick={handleNavigationClick}
                >
                    <IconChevronLeft size={32} />
                </button>
                <div className='overflow-hidden h-[238px]'>
                    <div
                        ref={videoListDivRef}
                        className='h-[254px] relative z-0 grid auto-cols-[250px] grid-flow-col gap-4 overflow-x-auto overflow-y-hidden pt-2'
                    >
                        {getPostArray(15, posts).map((post) =>
                            postToArticle(
                                { section: 'video', post, locale },
                                {
                                    useHero: true,
                                    media: {
                                        options: {
                                            width: 240,
                                            height: 134,
                                        },
                                    },
                                    showPlayButton: true,
                                    showDescription: false,
                                    classes: { image: { 'w-full': true, 'max-h-[132px]': true }, title: { 'md:text-base': false } },
                                    hideMobileImage: false,
                                },
                            ),
                        )}
                    </div>
                </div>
                <button
                    type='button'
                    aria-label='Scroll right'
                    className={cn(
                        'hidden md:flex h-[238px] flex items-center hover:cursor-pointer pb-16 border-0 bg-transparent absolute top-0 right-0 translate-x-full',
                        {
                            'md:invisible': scrollDistance === 'max',
                        },
                    )}
                    data-direction='right'
                    onClick={handleNavigationClick}
                >
                    <IconChevronRight size={32} />
                </button>
            </div>
        </div>
    )
}
